import { useCallback, useState } from 'react';
import { listarDepositos } from '../api/depositos.js';
import type { Deposito, SituacaoDeposito } from '../api/depositos.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../api/paginacao.js';
import { seInformado } from '../api/recorte.js';
import { useConsulta } from '../api/useConsulta.js';
import { usePode } from '../autorizacao/usePode.js';
import { AvisoDaTela } from '../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../componentes/AvisoDaTela.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import { formatarDataHora } from '../componentes/datas.js';
import { filialDoFiltro, rotuloDaFilial } from '../componentes/deposito.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../componentes/FaixaDeFiltros.js';
import {
  CampoBusca,
  CampoFilial,
  ContadorRegistros,
  FiltroSelecao,
} from '../componentes/Filtros.js';
import { IconeArmazem } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Paginacao } from '../componentes/Paginacao.js';
import { Resultado } from '../componentes/Resultado.js';
import { Tabela } from '../componentes/Tabela.js';
import type { Coluna } from '../componentes/Tabela.js';
import { ImportarDoErp } from './depositos/ImportarDoErp.js';
import { ReservasDeContagem } from './depositos/ReservasDeContagem.js';
import './Depositos.css';

const TODAS = 'todas';

const OPCOES_DE_SITUACAO = [
  { valor: TODAS, rotulo: 'Situação · todas' },
  { valor: 'Ativo', rotulo: 'Ativo' },
  { valor: 'Inativo', rotulo: 'Inativo' },
];

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

/**
 * Depósitos do ERP, por filial (RF-083).
 *
 * O local de toda operação é o par filial + depósito do Senior. Esta tela é SÓ
 * LEITURA: a lista vem do ERP pela sincronização, e é dela que o coletor baixa
 * os depósitos que aceita. Depósito fora dela, ou inativo, é recusado no
 * coletor. Não há cadastro manual — um depósito que o ERP não conhece não
 * receberia o lançamento que a operação gera.
 *
 * Abaixo da lista, os depósitos travados para contagem agora.
 */
export function Depositos() {
  const pode = usePode();

  const [busca, definirBusca] = useState('');
  const [filial, definirFilial] = useState('');
  const [situacao, definirSituacao] = useState<string>(TODAS);
  const [pagina, definirPagina] = useState(1);
  const [aviso, definirAviso] = useState<Aviso | null>(null);

  const codFil = filialDoFiltro(filial);

  const carregar = useCallback(
    () =>
      listarDepositos({
        pagina,
        ...seInformado('busca', busca),
        ...seInformado('codFil', codFil),
        ...seInformado('situacao', situacao === TODAS ? null : (situacao as SituacaoDeposito)),
      }),
    [pagina, busca, codFil, situacao],
  );

  const consulta = useConsulta(carregar);
  const { recarregar } = consulta;

  function trocarFiltro(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  const colunas: readonly Coluna<Deposito>[] = [
    {
      chave: 'filial',
      rotulo: 'Filial',
      largura: '12%',
      celula: (deposito) => (
        <span className="depositos__filial">{rotuloDaFilial(deposito.codFil)}</span>
      ),
    },
    {
      chave: 'deposito',
      rotulo: 'Depósito',
      largura: '14%',
      celula: (deposito) => <span className="depositos__codigo">{deposito.codDep}</span>,
    },
    {
      chave: 'nome',
      rotulo: 'Nome no ERP',
      largura: '34%',
      celula: (deposito) => (
        <span>
          {deposito.desDep}{' '}
          {deposito.abrDep !== '' && (
            <span className="depositos__discreto">({deposito.abrDep})</span>
          )}
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      largura: '14%',
      celula: (deposito) =>
        deposito.ativo ? (
          <ChipStatus tom="concluida" rotulo="Ativo" tamanho="tabela" />
        ) : (
          <ChipStatus tom="desativado" rotulo="Inativo" tamanho="tabela" />
        ),
    },
    {
      chave: 'sincronizado',
      rotulo: 'Sincronizado em',
      celula: (deposito) => (
        <span className="depositos__discreto">{formatarDataHora(deposito.sincronizadoEm)}</span>
      ),
    },
  ];

  return (
    <Pagina
      contexto="Depósitos"
      titulo="Depósitos por filial"
      faixa={
        <p className="depositos__explicacao">
          Os depósitos de cada filial, como estão no ERP. O coletor só aceita operação em depósito
          ativo desta lista.
        </p>
      }
    >
      <div className="depositos">
        <FaixaDeFiltros rotulo="Filtros de depósitos">
          <CampoBusca
            rotulo="Buscar por código ou nome do depósito"
            valor={busca}
            largura={280}
            aoMudar={(valor) => {
              trocarFiltro(() => {
                definirBusca(valor);
              });
            }}
          />
          <CampoFilial
            valor={filial}
            aoMudar={(valor) => {
              trocarFiltro(() => {
                definirFilial(valor);
              });
            }}
          />
          <FiltroSelecao
            rotulo="Filtrar por situação do depósito"
            valor={situacao}
            opcoes={OPCOES_DE_SITUACAO}
            aoMudar={(valor) => {
              trocarFiltro(() => {
                definirSituacao(valor);
              });
            }}
          />
          {consulta.dados !== null && (
            <ContadorRegistros>
              {contagemDoRecorte(consulta.dados.total, 'depósito', 'depósitos')}
            </ContadorRegistros>
          )}
          {pode('depositos.importarDoErp') && (
            <span className="depositos__acao-principal">
              <ImportarDoErp
                aoImportar={(resultado) => {
                  definirAviso(resultado);
                  recarregar();
                }}
                aoFalhar={(texto) => {
                  definirAviso({ tom: 'erro', texto });
                }}
              />
            </span>
          )}
        </FaixaDeFiltros>

        {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

        <Resultado
          consulta={consulta}
          espera="Carregando depósitos…"
          estaVazio={(dados) => dados.itens.length === 0}
          vazio={
            <EstadoVazio
              tom="neutro"
              icone={<IconeArmazem tamanho={22} />}
              titulo="Nenhum depósito neste recorte"
              detalhe="Sem depósito na lista, o coletor recusa movimentação, coleta e contagem. A lista vem do ERP: o Administrador sincroniza com a integração ligada."
            />
          }
        >
          {(dados) => (
            <Tabela
              itens={dados.itens}
              colunas={colunas}
              chaveDe={(deposito) => deposito.id}
              descricao="Depósitos por filial"
              rodape={
                <Paginacao
                  intervalo={intervaloDaPagina(dados, 'depósito', 'depósitos')}
                  pagina={dados.pagina}
                  totalPaginas={totalDePaginas(dados)}
                  aoIrPara={definirPagina}
                />
              }
            />
          )}
        </Resultado>

        <ReservasDeContagem codFil={codFil} />
      </div>
    </Pagina>
  );
}
